import React from 'react';
import { formatGrantRate } from '../utils/freeTicketRequirements';
import { formatTierLadder } from '../utils/nftBonusConfig';

function formatNextGrant(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

function userHasLinkedWallet(user) {
  return !!(user?.wallets?.length || user?.walletAddress);
}

/**
 * NFT / FT holder bonus table. Grants accrue via linked wallets checked on-chain by the backend.
 */
export default function NftHolderBonusesSection({
  nftBonuses = [],
  user,
  account,
  verifying = false,
  onConnectWallet,
  linkingWallet = false,
  isConnecting = false,
  compact = false,
}) {
  const linkedWallet = userHasLinkedWallet(user);

  const rowStatus = (n) => {
    if (!user) return { key: 'login', label: 'Log in' };
    if (!linkedWallet && !account && n.holds !== true && n.holds !== false) {
      return { key: 'link', label: 'Link wallet' };
    }
    if (n.holds === true || (n.verifiedOnChain && n.holds)) {
      return { key: 'active', label: 'Active' };
    }
    if (n.holds === false || (n.verifiedOnChain && !n.holds)) {
      if (n.holdsOnConnectedOnly) return { key: 'link', label: 'Held — link wallet' };
      return { key: 'none', label: 'Not held' };
    }
    if (verifying && linkedWallet) return { key: 'checking', label: 'Checking…' };
    if (!linkedWallet && account) return { key: 'link', label: 'Link wallet' };
    if (!linkedWallet) return { key: 'link', label: 'Link wallet' };
    return { key: 'pending', label: '—' };
  };

  const statusClass = (key) => {
    if (key === 'active') {
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200';
    }
    if (key === 'link') {
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200';
    }
    if (key === 'checking') {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
    }
    return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          NFT / FT holder bonuses
        </h3>
        {!linkedWallet && user && onConnectWallet && (
          <button
            type="button"
            onClick={onConnectWallet}
            disabled={linkingWallet || isConnecting}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 shrink-0"
          >
            {linkingWallet || isConnecting ? 'Connecting…' : 'Connect & link wallet'}
          </button>
        )}
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 leading-relaxed">
        Link your wallet once — the platform verifies holdings on-chain when your grant period is due
        (ERC-721, ERC-1155, ERC-20, and ERC-6551 Token Bound Accounts).
      </p>
      {user && !linkedWallet && (
        <p className="text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 mb-3 text-xs leading-relaxed">
          Connect and link your wallet to your account. Supports ERC-721 NFTs, ERC-1155/ERC-20 tokens, and
          ERC-6551 token-bound accounts. Bonus tickets accrue in the background while you hold eligible assets.
        </p>
      )}
      {linkedWallet && (
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
          Linked wallet{user.wallets?.length > 1 ? 's' : ''} verified on-chain. Higher holdings unlock better
          reward tiers automatically.
        </p>
      )}
      {nftBonuses.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-600">
          <table className={`w-full ${compact ? 'text-xs' : 'text-xs'} min-w-[560px]`}>
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr>
                <th className="text-left p-2.5">Collection</th>
                <th className="text-right p-2.5 whitespace-nowrap">Your rate</th>
                <th className="text-center p-2.5 whitespace-nowrap">Status</th>
                <th className="text-right p-2.5 hidden md:table-cell whitespace-nowrap">Next grant</th>
                <th className="text-right p-2.5" />
              </tr>
            </thead>
            <tbody>
              {nftBonuses.map((n, i) => {
                const st = rowStatus(n);
                const normalRate = formatGrantRate(
                  n.normalTickets ?? n.dailyTickets,
                  n.normalPeriodDays || 3
                );
                const goldenRate = formatGrantRate(n.goldenTickets, n.goldenPeriodDays || 3);
                const nextLabel =
                  st.key === 'active'
                    ? n.goldenTickets > 0
                      ? `Golden ${formatNextGrant(n.nextGoldenGrantAt)}`
                      : `Normal ${formatNextGrant(n.nextNormalGrantAt)}`
                    : '—';
                const tierLadder = formatTierLadder(n);

                return (
                  <tr key={n.id || i} className="border-t border-slate-100 dark:border-slate-700">
                    <td className="p-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        {n.imageUrl ? (
                          <img src={n.imageUrl} alt="" className="w-9 h-9 rounded object-cover shrink-0" />
                        ) : (
                          <span className="w-9 h-9 rounded bg-slate-200 dark:bg-slate-600 shrink-0 flex items-center justify-center text-slate-400 text-[10px]">
                            NFT
                          </span>
                        )}
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white truncate">
                            {n.name || 'Collection'}
                          </div>
                          {n.contractAddress && (
                            <div className="font-mono text-[10px] text-slate-500 truncate" title={n.contractAddress}>
                              {String(n.contractAddress).slice(0, 6)}…{String(n.contractAddress).slice(-4)}
                            </div>
                          )}
                          {st.key === 'active' && (n.holdingsCount || 0) > 0 && (
                            <div className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">
                              Holding {n.holdingsCount}
                              {n.activeTierMinHoldings > 0 ? ` (tier ${n.activeTierMinHoldings}+)` : ''}
                            </div>
                          )}
                          {tierLadder && (
                            <div className="text-[10px] text-slate-400 mt-0.5" title="Tier thresholds">
                              Tiers: {tierLadder}
                            </div>
                          )}
                          {st.key === 'active' && (n.normalAvailable || 0) > 0 && (
                            <div className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">
                              {n.normalAvailable} normal available now
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-2.5 text-right tabular-nums">
                      <div className="font-semibold text-emerald-700 dark:text-emerald-300">{normalRate}</div>
                      <div className="text-[10px] text-slate-500">normal · refreshes</div>
                      {(n.goldenTickets || 0) > 0 && (
                        <>
                          <div className="font-semibold text-amber-700 dark:text-amber-300 mt-1">{goldenRate}</div>
                          <div className="text-[10px] text-slate-500">golden · accumulates</div>
                        </>
                      )}
                    </td>
                    <td className="p-2.5 text-center whitespace-nowrap">
                      <span
                        className={`inline-flex min-w-[5rem] max-w-full justify-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${statusClass(st.key)}`}
                      >
                        {st.label}
                      </span>
                    </td>
                    <td className="p-2.5 text-right hidden md:table-cell text-[10px] text-slate-500 whitespace-nowrap">
                      {nextLabel}
                    </td>
                    <td className="p-2.5 text-right">
                      {n.link ? (
                        <a
                          href={n.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline font-medium whitespace-nowrap"
                        >
                          Get
                        </a>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-500 dark:text-slate-400 italic rounded-lg border border-dashed border-slate-300 dark:border-slate-600 p-4 text-center">
          No NFT / FT bonus collections configured yet.
        </p>
      )}
    </div>
  );
}
